import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import UploadLarge from '@ui/uploads/upload-large';
import {
  GetUploadEndpoint,
  getUploadFilename,
  UploadFileToProvider,
  SetItemContentType,
} from '@ui/uploads/validations';
import { memo, useState } from 'react';
import { isObject } from 'underscore';
import Text from '@ui/text/text/text';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import { GetFontName } from '@/connection/font-packs';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';

interface IUploadNewFontProps {
  afterUpload: (option: TReactSelectOption<string, string, { url: string; name: string }>) => void;
  onUploadStart?: () => void;
}

const UploadNewFont = memo(({ afterUpload, onUploadStart }: IUploadNewFontProps) => {
  const [uploading, setUploading] = useState(false);

  // we create a new font pack where the heading and body are the same
  const onFile = async (file: File | FileList | null) => {
    setUploading(true);
    onUploadStart?.();

    let option: TReactSelectOption<string, string, { url: string; name: string }> | null = null;
    try {
      if (!file || file instanceof FileList) {
        throw new Error('Invalid file');
      }

      const endpoint = await GetUploadEndpoint({
        filename: getUploadFilename(file.name),
      });

      if (isObject(endpoint) && endpoint.url) {
        const finalUrl = await UploadFileToProvider(
          {
            url: endpoint.url,
            fields: endpoint.fields,
            cdnHost: endpoint?.cdnHost,
          },
          file
        );

        SetItemContentType({ filename: finalUrl, contentType: file.type }).catch(console.error);

        const name = await GetFontName(finalUrl);
        option = {
          label: name.name,
          value: finalUrl,
          meta: { url: finalUrl, name: name.name },
        } as TReactSelectOption<string, string, { url: string; name: string }>;
      }
    } catch (error) {
      console.error(error);
      toastTrigger('There was a problem uploading the font file', {
        type: EToastTypes.error,
        autoClose: 10000,
      });
    } finally {
      setUploading(false);

      if (afterUpload && option) {
        afterUpload(option);
      }
    }
  };

  return (
    <Container height="32px" border="none" hoverBackground="var(--gray-700)">
      <UploadLarge
        uploading={uploading}
        noGradients={true}
        isFocusable
        standard
        CustomTitle={
          <Container className="P2 flex fd-row jc-flex-start ai-center width-100-percent padding-l-12" height="32px">
            <Icon name={ICONS.ADD} size={16} className="margin-r-6" color="var(--local-bttn-prim-default-bg)" />
            <Text color="var(--local-bttn-prim-default-bg)"> Add new</Text>
          </Container>
        }
        onFile={onFile}
        allowedFileTypes={['font/ttf', 'font/otf', 'font/woff', 'application/font-woff']}
        allowedFileExtensions={['.ttf', '.otf', '.woff']}
        noShadow
        buttonHoverBackgroundColor="var(--gray-700)"
        buttonBorderRadius="0px"
      />
    </Container>
  );
});

export default UploadNewFont;
